import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import * as TablerIcons from '@ng-icons/tabler-icons';

type IconSet = Record<string, string>;

const TABLER_ICONS: IconSet = Object.entries(TablerIcons).reduce<IconSet>((acc, [key, value]) => {
  if (typeof value !== 'string' || !key.startsWith('tabler')) {
    return acc;
  }

  acc[normalizeName(key.slice('tabler'.length))] = value;
  return acc;
}, {});

function normalizeName(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

@Component({
  selector: 'i-tabler, tabler-icon',
  standalone: true,
  imports: [NgIcon, NgClass],
  template: `
    <ng-icon
      [svg]="svg()"
      [size]="size()"
      [strokeWidth]="strokeWidth()"
      [color]="color()"
      [ngClass]="hostClasses()"
    />
  `,
  styles: `
    :host {
      display: inline-flex;
      line-height: 0;
      vertical-align: middle;
    }

    :host ng-icon {
      display: inline-flex;
      color: inherit;
      fill: inherit;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TablerIconComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private observer?: MutationObserver;

  readonly name = input<string>('');
  readonly size = input<string>('');
  readonly strokeWidth = input<string>('');
  readonly color = input<string>('');

  readonly hostClasses = signal('');
  readonly svg = computed(() => {
    const iconName = this.name();
    if (!iconName) {
      return '';
    }

    return TABLER_ICONS[normalizeName(iconName)] ?? '';
  });

  ngAfterViewInit(): void {
    const updateHostClasses = () => {
      this.hostClasses.set(this.host.nativeElement.className);
    };

    updateHostClasses();
    this.observer = new MutationObserver(updateHostClasses);
    this.observer.observe(this.host.nativeElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
